// ══════════════════════════════════════════════════════════════════
//  밤 들판 바닥 — **표는 여기 하나다**
//
//  ■ 왜 따로 뺐나 【2026-08-02】
//    구 구현은 같은 목록을 **두 곳**에 손으로 적고 있었다:
//      · `scene.js` 의 `FIELD_MASTERS`     — 게임이 실제로 로드하는 것
//      · `scripts/prepare-sites.mjs`        — 배포본에 복사하는 것
//    그 둘이 갈리면 **개발 서버에서는 보이는데 배포본에서만 바닥이 없다.**
//    이 저장소는 그 사고를 부품 아이콘에서 이미 한 번 겪었고(`gearart.js` 주석),
//    `prepare-sites` 자신이 *"여기 손으로 적으면 표가 둘이 된다"* 고 경고해 두었다.
//    그런데 들판은 그 경고 밖에 있었다. **이제 둘 다 이 파일을 읽는다.**
//    ⚠ `scene.js` 는 Phaser 를 import 하므로 노드에서 못 읽는다 — 그래서 **표만**
//      여기 두어야 배포 스크립트가 같은 것을 볼 수 있다(gearart 와 같은 문법).
//
//  ■ **이어붙여도 자국이 없는 판을 쓴다** 【사장님 지적 2026-08-02】
//    벽을 없애며 바닥을 무한 반복하게 됐는데, 마스터는 「한 장면으로 그린 그림」이라
//    이어붙이면 **십자 자국**이 떴다. `tools/make_seamless_field.py` 가 씻어 낸 판이
//    `-seamless-v1` 이다.
//    ⚠ **자국은 두 가지다. 하나만 잡으면 사장님 눈에 걸린다** — 1판이 그랬다.
//      · 이음매(맞닿는 변)  2.95~4.13 → **0.84~1.06**  (1.0 = 안쪽과 구별 안 됨)
//      · 조명 얼룩(저주파)   9.4~28.6 → **1.9~6.5 %**  ← 1판이 놓친 것
//      1판은 이음매만 1.0 으로 만들었는데 얼룩이 남아 **960px 주기로 되풀이돼서**
//      격자로 읽혔다(사장님: *"아주 살짝 남아있어"*). 2판이 얼룩을 원본의 30% 로
//      내렸다. 도구가 이제 **두 숫자를 같이 찍는다** — 하나만 보면 또 속는다.
//    ⚠ **원본은 지우지 않았다.** 되돌리려면 아래 이름에서 `-seamless-v1` 만 떼면 된다.
// ══════════════════════════════════════════════════════════════════

import { chapterOf } from './config.js';

/** 장(1~6)마다 한 장. 인덱스 0 = 1장. */
export const FIELD_MASTERS = [
  'night-open-plain-master-960x540-v3-seamless-v1.png',
  'night-ch2-dry-wilderness-master-960x540-v2-seamless-v1.png',
  'night-ch3-gloomy-valley-master-960x540-v2-seamless-v1.png',
  'night-ch4-renewal-master-960x540-v1-seamless-v1.png',
  'night-ch5-war-torn-land-master-960x540-v2-seamless-v1.png',
  'night-ch6-valleys-eve-master-960x540-v1-seamless-v1.png',
];

/** `art.html` 비교실이 고를 수 있는 것들 — 게임은 안 쓰지만 배포본에는 있어야 한다. */
export const FIELD_EXTRAS = [
  'night-flat-open-pasture-v3.png',
  'night-flat-grass-v2.png',
  'night-flat-dry-pasture-v2.png',
  'night-flat-gravel-v2.png',
  'night-flat-dry-wash-v3.png',
  'night-flat-hill-mood-v3.png',
  'night-open-plain-v1.png',
  'night-ravine-v1.png',
  'night-hill-v1.png',
];

/**
 * 그 밤이 쓰는 바닥.
 * ⚠ **장 구분을 여기서 다시 세지 않는다** 【40일 개편 2026-08-03】. 구 판은
 *   `Math.ceil(밤/5)` 를 손으로 적어 두었는데, 40일에서 장은 **안식일이 끊는다**
 *   (7일마다). 그대로 뒀으면 들판이 장과 어긋나 **안식일 다음 날에도 같은 바닥**이
 *   깔렸을 것이다. 정본은 `config.chapterOf` 하나다.
 */
export function fieldMasterFor(nightKey) {
  const nightNo = Number.parseInt(String(nightKey).replace(/\D/g, ''), 10) || 1;
  return FIELD_MASTERS[Math.max(1, Math.min(6, chapterOf(nightNo))) - 1];
}

/** 배포본에 복사할 바닥 파일 전부 — `prepare-sites` 가 이걸 읽는다. */
export function fieldArtFiles() {
  return [...FIELD_MASTERS, ...FIELD_EXTRAS].map((f) => `art/fields/${f}`);
}
